import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Typography,
  Box,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { I18n } from "aws-amplify/utils";
import InfoIcon from "@mui/icons-material/Info";
import { useTemplateController } from "context";
import AccountListPopup from "./AccountListPopup";
import ConfirmationDialog from "./ConfirmationDialog";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { get } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";

function AddNewClosureDialog({ isOpen, onClose, handleSubmitRule, mainFormData, type }) {
  const [isAccountListOpen, setIsAccountListOpen] = useState(false);
  const [controller] = useTemplateController();
  const { darkMode } = controller;
  const [confirmation, setConfirmation] = useState(false);
  const [accountList, setAccountList] = useState([]);
  const [errorAccount, setErrorAccount] = useState(false);

  const [formData, setFormData] = useState({
    description: null,
    account_number: null,
    original_store: null,
    original_priority: null,
    original_equipment: null,
    alarm_text: null,
    severity: null,
    position: null,
  });

  useEffect(() => {
    if (type === "edit") {
      setFormData((currentState) => ({
        ...currentState,
        ...mainFormData,
      }));
    }
  },[]);

  const handleMainFormData = (field, value) => {
    setFormData((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const handleAddRule = () => {
    // Trim whitespace and replace multiple spaces with a single space
    // Convert empty strings to null
    const trimmedFormData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => {
        if (typeof value === "string") {
          const trimmed = value.trim().replace(/\s\s+/g, " ");
          return [key, trimmed === "" ? null : trimmed];
        }
        return [key, value];
      })
    );
    handleSubmitRule(trimmedFormData);
  }

  const handleSaveAccountNumber = (formData) => {
    handleMainFormData("account_number", formData.account_number);
    setIsAccountListOpen(false);
  }

  const fetchAccountNumbers = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/accountNumber/get",
        options: {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${await currentUserToken()}`,
        },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      setAccountList(response);
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  useEffect(() => {
    fetchAccountNumbers();
  },[]);

  useEffect(() => {
    if(formData.account_number !== "" && formData.account_number !== null) {
      if( accountList.find(account => account.account_number === formData.account_number)) {
        setErrorAccount(false);
      } else {
        setErrorAccount(true);
      }
    }else{
      setErrorAccount(false);
    }
  },[formData.account_number]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
    >
      <DialogTitle>
        { type === "edit" ? I18n.get("Edit Rule") : I18n.get("Add New Closure Rule")}
      </DialogTitle>
      <DialogContent>
        <Box
            display="flex"
            alignItems="center"
            bgcolor={darkMode ? "#27496d" : "lightblue"}
            p={2}
            borderRadius={1}
            mb={2}
        >
        <InfoIcon sx={{ mr: 1 }} />
        <Typography variant="body2">
          {type === "edit" 
            ? I18n.get("This section allows you to edit an automatic closure rule. Please edit carefully the fields below.") 
            : I18n.get("This section allows you to add a new automatic closure rule. Please fill carefully the fields below.")
          }
        </Typography>
        </Box>
        <FormControl fullWidth margin="small" className="custom-form-control">
          <TextField
            label={I18n.get("Description")}
            value={formData.description}
            fullWidth
            onChange={(e) => handleMainFormData("description", e.target.value)}
            margin="normal"
            name="description"
            className="custom-text-field"
            required
            error={!formData.description || formData.description === null}
          />
          <FormHelperText>{I18n.get("This field is required")}</FormHelperText>
        </FormControl>
        <Box display="flex" flexDirection="row"  gap={2} alignContent={"center"} alignItems={"center"} width="100%">
          <FormControl fullWidth margin="small" className="custom-form-control">
            <TextField
              label={I18n.get("Account Number")}
              value={formData.account_number || ""}
              onChange={(e) => handleMainFormData("account_number", e.target.value)}
              fullWidth
              margin="normal"
              name="asset"
              error={errorAccount}
              helperText={errorAccount ? I18n.get("Account number not found") : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SettingsSuggestIcon
                      style={{ cursor: "pointer" }}
                      onClick={() => setIsAccountListOpen(true)}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText>{I18n.get("Enter an account number or select from the list")}</FormHelperText>
          </FormControl>
        </Box>
        <FormControl fullWidth margin="small" className="custom-form-control">
            <TextField
                label={I18n.get("Store")}
                value={formData.original_store}
                fullWidth
                onChange={(e) => handleMainFormData("original_store", e.target.value)}
                margin="normal"
                name="original_store"
                className="custom-text-field"
            />
        </FormControl>
        <FormControl fullWidth margin="small" className="custom-form-control">
            <TextField
                label={I18n.get("Equipment")}
                value={formData.original_equipment}
                fullWidth
                onChange={(e) => handleMainFormData("original_equipment", e.target.value)}
                margin="normal"
                name="original_equipment"
                className="custom-text-field"
            />
        </FormControl>
        <FormControl fullWidth margin="small" className="custom-form-control">
            <TextField
                label={I18n.get("Alarm Text")}
                value={formData.alarm_text}
                fullWidth
                onChange={(e) => handleMainFormData("alarm_text", e.target.value)}
                margin="normal"
                name="alarm_text"
                className="custom-text-field"
            />
        </FormControl>
        <FormControl fullWidth margin="small" className="custom-form-control">
            <TextField
                label={I18n.get("Priority")}
                value={formData.original_priority}
                fullWidth
                onChange={(e) => handleMainFormData("original_priority", e.target.value)}
                margin="normal"
                name="original_priority"
                className="custom-text-field"
                type="number"
                inputProps={{
                    min: 1,
                    max: 4,
                    onKeyDown: (e) => {
                    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
                        e.preventDefault();
                    }
                    },
                }}
            />
        </FormControl>
        <FormControl fullWidth margin="small" className="custom-form-control">
            <TextField
                label={I18n.get("Severity")}
                value={formData.severity}
                fullWidth
                onChange={(e) => handleMainFormData("severity", e.target.value)}
                margin="normal"
                name="severity"
                className="custom-text-field"
                type="number"
                inputProps={{
                    min: 0,
                    max: 10,
                    onKeyDown: (e) => {
                    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") {
                        e.preventDefault();
                    }
                    },
                }}
            />
        </FormControl>
        <FormControl fullWidth margin="small" className="custom-form-control">
            <TextField
                label={I18n.get("Position")}
                value={formData.position}
                fullWidth
                onChange={(e) => handleMainFormData("position", e.target.value)}
                margin="normal"
                name="position"
                className="custom-text-field"

            />
        </FormControl>

      </DialogContent>
      <DialogActions sx={{ pt: 2 }}>
        <>
            <Button
              onClick={onClose}
              style={{ marginLeft: "10px", color: "red" }}
            >
              {I18n.get("Cancel")}
            </Button>
            <Button
              color="primary"
              onClick={() => { setConfirmation(true); }}
              disabled={ !formData.description || formData.description === null || errorAccount}
            >
              {I18n.get("Submit")}
            </Button>
        </>
      </DialogActions>
      {isAccountListOpen && (
        <AccountListPopup
          open={isAccountListOpen}
          onClose={() => setIsAccountListOpen(false)}
          handleSaveAccountNumber={handleSaveAccountNumber}
        />
      )}
      <ConfirmationDialog
        open={confirmation}
        onClose={() => setConfirmation(false)}
        onSave={handleAddRule}
        onCancel={() => setConfirmation(false)}
        type={type}
      />  
    </Dialog>
  );
}

AddNewClosureDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddNewClosureDialog;
